/**
 * Job detail page (T037, port of rm-ui/src/pages/job_detail.rs).
 *
 * Data via polling hooks (useJob / useJobLogs / useFrames) — no Electric sync.
 * Cancel via useMutation + query invalidation (qk.job / qk.jobs). DOM (testids,
 * classes, Polish text) matches the Playwright parity contract verbatim.
 *
 * Note vs Dioxus source: the View Tour button links to `job.viewer_url` (the C3
 * field), shown only when present — per the migration parity contract.
 */
import { useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useJob } from '../hooks/useJob'
import { useJobLogs } from '../hooks/useJobLogs'
import { cancelJob, rerunJob } from '../lib/endpoints/jobs'
import { qk } from '../lib/queryKeys'
import {
  displayName,
  isCancellable,
  jobDuration,
  JOB_TYPE_LABELS,
} from '../lib/jobView'
import { isTerminal } from '../lib/jobStatus'
import { ApiError } from '../lib/errors'
import { TESTID } from '../lib/testids'
import { StatusBadge } from '../components/StatusBadge'
import { JobDetailSkeleton } from '../components/Skeleton'
import { PipelineProgress } from '../components/PipelineProgress'
import { FrameGallery } from '../components/FrameGallery'
import { JobAttempts } from '../components/jobs/JobAttempts'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { JobTypeDetails } from '../components/jobs/JobTypeDetails'
import { JobReportSummary } from '../components/jobs/JobReportSummary'
import { useToast } from '../components/Toast'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { SheetBody, SheetHeader, SheetTitle } from '../components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  AlertTriangle,
  Ban,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  RotateCcw,
  SearchX,
} from 'lucide-react'
import type { Job, JobLog } from '../lib/schemas'

type JobDetailTab = 'overview' | 'logs'

/** Format created_at as `YYYY-MM-DD HH:MM` in local time (mirror time_format::local_datetime). */
function localDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function JobDetailPage({ id }: { id: string }) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const nav = useNavigate()
  const { data: job, error, isPending, refetch } = useJob(id)

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [activeTab, setActiveTab] = useState<JobDetailTab>('overview')

  const cancelMutation = useMutation({
    mutationFn: () => cancelJob(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.job(id) })
      void queryClient.invalidateQueries({ queryKey: qk.jobs() })
      showToast({ title: 'Job anulowany', variant: 'info' })
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Nie udalo sie anulowac'
      showToast({ title: message, variant: 'error' })
    },
  })

  // Rerun: create a new job with identical parameters, then jump to its preview.
  const rerunMutation = useMutation({
    mutationFn: () => rerunJob(id),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs() })
      showToast({ title: 'Utworzono nowy job (rerun)', variant: 'info' })
      void nav({ to: '/jobs', search: { preview: res.data.id } })
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Nie udalo sie powtorzyc'
      showToast({ title: message, variant: 'error' })
    },
  })

  // Not-found is ONLY a real 404 — a 429 (rate limit) or other transient error
  // must not masquerade as "job not found"; those show a retryable error state
  // while the poll recovers on its own.
  const isNotFound = error instanceof ApiError && error.code === 'NotFound'
  const loadError = !job && !isPending && !!error && !isNotFound

  if (!job) {
    if (isNotFound) {
      return (
        <>
          <SheetHeader>
            <SheetTitle>Zadanie nie znalezione</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="text-center py-16" data-testid={TESTID.jobNotFound}>
              <SearchX className="mx-auto mb-3 size-10 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground">
                Job nie znaleziony
              </h3>
              <p className="text-muted-foreground mt-2">ID: {id}</p>
            </div>
          </SheetBody>
        </>
      )
    }
    if (loadError) {
      const isRateLimited = error instanceof ApiError && error.status === 429
      return (
        <>
          <SheetHeader>
            <SheetTitle>Nie udalo sie zaladowac</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div
              className="text-center py-16"
              data-testid={TESTID.jobLoadError}
            >
              <AlertTriangle className="mx-auto mb-3 size-10 text-muted-foreground" />
              <p className="text-muted-foreground">
                {isRateLimited
                  ? 'Zbyt wiele zapytan — sprobuj za chwile.'
                  : 'Wystapil blad przy ladowaniu zadania.'}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => void refetch()}
              >
                Sprobuj ponownie
              </Button>
            </div>
          </SheetBody>
        </>
      )
    }
    return (
      <>
        <SheetHeader>
          <SheetTitle className="sr-only">Ladowanie zadania</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <JobDetailSkeleton />
        </SheetBody>
      </>
    )
  }

  const name = displayName(job)
  const createdAt = localDateTime(job.created_at)
  const duration = jobDuration(job)
  const isRendering = job.status === 'rendering'
  const isFailed = job.status === 'failed'
  const isDone = job.status === 'done'
  const cancellable = isCancellable(job)
  const errorMsg = job.error_message ?? null

  // Progress bar visible only while actively progressing.
  const showProgress =
    !isDone &&
    !isFailed &&
    job.status !== 'preparing' &&
    job.status !== 'queued' &&
    job.status !== 'cancelled'

  const showGallery =
    isDone || isRendering || isFailed || job.status === 'uploading'

  const viewerUrl = job.viewer_url ?? null

  const confirmCancel = () => {
    setShowCancelDialog(false)
    cancelMutation.mutate()
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle
          className="truncate text-lg"
          data-testid={TESTID.jobDetailName}
        >
          {name}
        </SheetTitle>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StatusBadge status={job.status} />
          <Badge variant="outline">{JOB_TYPE_LABELS[job.job_spec.type]}</Badge>
          <span className="tabular-nums">{createdAt}</span>
          {duration && (
            <span
              className="inline-flex items-center gap-1 tabular-nums"
              title="Czas trwania"
              data-testid="job-duration"
            >
              <Clock className="size-3" />
              {duration}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">ID: {job.id}</p>
      </SheetHeader>

      <SheetBody>
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as JobDetailTab)}
        >
          <TabsList variant="line" className="h-auto">
            <TabsTrigger
              value="overview"
              className="rounded-full px-3"
              data-testid={TESTID.jobDetailTabOverview}
            >
              Szczegoly
            </TabsTrigger>
            <TabsTrigger
              value="logs"
              className="rounded-full px-3"
              data-testid={TESTID.jobDetailTabLogs}
            >
              Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {showProgress && (
              <PipelineProgress
                status={job.status}
                progress={job.progress}
                setupStage={job.setup_stage ?? null}
                setupPercent={job.setup_percent ?? null}
                progressPercent={job.progress_percent ?? 0}
              />
            )}

            {isFailed && errorMsg && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                <h4 className="flex items-center gap-1.5 text-sm font-medium text-destructive mb-1">
                  <AlertTriangle className="size-4" />
                  Blad renderingu
                </h4>
                <p className="text-sm text-destructive">{errorMsg}</p>
              </div>
            )}

            <Card size="sm" data-testid={TESTID.jobParameters}>
              <CardHeader>
                <CardTitle>
                  <h3>Parametry zadania</h3>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <JobTypeDetails job={job} />
              </CardContent>
            </Card>

            <JobAttempts job={job} />

            {/* Worker-reported summary (RM-117) — shown only when a worker posted
              one; shape is worker-decided and rendered generically. */}
            {job.job_summary != null && (
              <div
                className="bg-card rounded-lg border border-border p-4"
                data-testid="job-report-summary"
              >
                <h3 className="text-sm font-medium text-foreground mb-3">
                  Podsumowanie
                </h3>
                <JobReportSummary summary={job.job_summary} />
              </div>
            )}

            {showGallery && <FrameGallery jobId={job.id} />}

            {viewerUrl && (
              <a
                className="inline-flex items-center gap-2 px-5 py-3 bg-success text-success-foreground rounded-lg hover:bg-success/80 font-semibold text-sm transition-colors"
                href={viewerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TESTID.viewTourButton}
              >
                <ExternalLink className="size-4" />
                View Tour
              </a>
            )}

            <div className="flex gap-3">
              {isFailed && (
                <Button
                  variant="outline"
                  data-testid={TESTID.rerunButton}
                  onClick={() => rerunMutation.mutate()}
                  disabled={rerunMutation.isPending}
                >
                  <RotateCcw />
                  {rerunMutation.isPending ? 'Powtarzam…' : 'Powtórz'}
                </Button>
              )}
              {cancellable && (
                <Button
                  variant="destructive"
                  data-testid={TESTID.cancelButton}
                  onClick={() => setShowCancelDialog(true)}
                >
                  <Ban />
                  Anuluj
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <JobLogsPanel jobId={job.id} status={job.status} />
          </TabsContent>
        </Tabs>

        {showCancelDialog && (
          <Dialog
            open
            onOpenChange={(o) => {
              if (!o) setShowCancelDialog(false)
            }}
          >
            <DialogContent
              showCloseButton={false}
              data-testid={TESTID.cancelDialog}
            >
              <DialogHeader>
                <DialogTitle>Anulowac render?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Ta operacja nie moze byc cofnieta.
              </p>
              <DialogFooter>
                <Button
                  variant="ghost"
                  data-testid={TESTID.cancelDismiss}
                  onClick={() => setShowCancelDialog(false)}
                >
                  Nie
                </Button>
                <Button
                  variant="destructive"
                  data-testid={TESTID.cancelConfirm}
                  onClick={confirmCancel}
                >
                  Tak, anuluj
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </SheetBody>
    </>
  )
}

/**
 * Execution logs panel. Port of JobLogsPanel — uses the polling `useJobLogs`
 * hook instead of Electric sync. Lines are rendered as TEXT (rule C8 — never
 * dangerouslySetInnerHTML). Filter narrows visible lines; search highlights +
 * steps through matches within the visible set.
 */
function JobLogsPanel({
  jobId,
  status,
}: {
  jobId: string
  status: Job['status']
}) {
  const { data, isPending } = useJobLogs(jobId)
  const scrollRef = useRef<HTMLDivElement>(null)

  const [filterQuery, setFilterQuery] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeMatch, setActiveMatch] = useState(0)

  const logs: JobLog[] = useMemo(
    () => [...(data?.lines ?? [])].sort((a, b) => a.seq - b.seq),
    [data],
  )

  const filterText = filterQuery.trim().toLowerCase()
  const searchText = searchQuery.trim().toLowerCase()

  const filteredLogs = useMemo(
    () =>
      logs.filter(
        (log) =>
          filterText === '' || log.line.toLowerCase().includes(filterText),
      ),
    [logs, filterText],
  )

  const matchingIds = useMemo(
    () =>
      searchText === ''
        ? []
        : filteredLogs
            .filter((log) => log.line.toLowerCase().includes(searchText))
            .map((log) => log.id),
    [filteredLogs, searchText],
  )

  const matchCount = matchingIds.length
  const activeMatchIdx =
    matchCount === 0 ? null : Math.min(activeMatch, matchCount - 1)
  const activeMatchId =
    activeMatchIdx === null ? null : matchingIds[activeMatchIdx]

  const settled = !!data || !isPending
  const isLoading = filteredLogs.length === 0 && !settled
  const isEmpty = filteredLogs.length === 0 && settled
  const isLive = !isTerminal(status)
  const hasFilter = filterText !== ''

  return (
    <div
      className="bg-card rounded-lg border border-border overflow-hidden"
      data-testid={TESTID.jobLogsPanel}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-medium text-foreground">
            Logi wykonania
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {isLive
              ? 'Strumien aktualizuje sie na biezaco'
              : 'Zapis logow zakonczonego joba'}
          </p>
        </div>
        <span
          className={
            isLive
              ? 'inline-flex items-center gap-2 rounded-full bg-success/10 px-2.5 py-1 text-[11px] font-medium text-success'
              : 'inline-flex items-center gap-2 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground'
          }
        >
          {isLive ? (
            <>
              <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
              LIVE
            </>
          ) : (
            'Zakonczone'
          )}
        </span>
      </div>

      <div className="grid gap-3 border-b border-border bg-card px-4 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <div className="space-y-1">
          <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Filter
          </label>
          <Input
            type="text"
            placeholder="Pokaz tylko linie zawierajace tekst..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Search
          </label>
          <Input
            type="text"
            placeholder="Znajdz w widocznych liniach..."
            value={searchQuery}
            onChange={(e) => {
              setActiveMatch(0)
              setSearchQuery(e.target.value)
            }}
          />
        </div>
        <div className="flex items-end gap-2">
          <div className="min-w-16 pb-2 text-right text-xs text-muted-foreground">
            {matchCount > 0
              ? `${(activeMatchIdx ?? 0) + 1}/${matchCount}`
              : '0/0'}
          </div>
          <Button
            variant="outline"
            size="icon"
            disabled={matchCount === 0}
            onClick={() => {
              if (matchCount === 0) return
              const current = activeMatchIdx ?? 0
              setActiveMatch(current === 0 ? matchCount - 1 : current - 1)
            }}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={matchCount === 0}
            onClick={() => {
              if (matchCount === 0) return
              const current = activeMatchIdx ?? 0
              setActiveMatch(current + 1 >= matchCount ? 0 : current + 1)
            }}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div
              key={i}
              className="h-4 w-full rounded bg-muted animate-pulse"
            />
          ))}
        </div>
      ) : isEmpty ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {hasFilter
              ? 'Brak linii pasujacych do aktywnego filtra.'
              : isLive
                ? 'Logi pojawia sie tutaj po starcie maszyny renderujacej.'
                : 'Ten job nie zapisal jeszcze zadnych logow.'}
          </p>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="max-h-[70vh] overflow-auto bg-black/60"
          data-testid={TESTID.jobLogsScroll}
        >
          <div className="min-h-[12rem] p-4 font-mono text-xs leading-5 text-success">
            {filteredLogs.map((log) => {
              const isMatch =
                searchText !== '' && log.line.toLowerCase().includes(searchText)
              const isActive = activeMatchId === log.id
              const lineClass = isActive
                ? 'rounded bg-success/20 ring-1 ring-success/70'
                : isMatch
                  ? 'rounded bg-white/10'
                  : ''
              return (
                <div
                  key={log.id}
                  className={`whitespace-pre-wrap break-words px-2 py-0.5 ${lineClass}`}
                  data-log-line-id={log.id}
                >
                  {log.line}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
